import React, { useEffect, useState, useRef } from "react";
import {
  TextField, Button, Grid, Box, Switch, FormControlLabel, FormControl, Checkbox
} from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import { ShippingAgentViewModel } from "../../../Types/ExportTypes";
import { CountryAutoComplete, ShippingAgentExtSystemAutoComplete } from "../../../components/AutoComplete/AutoCompletes";
import DefaultActions from "../../../components/Card/DefaultActions";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";
const ShippingAgentEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { code = "" } = params || {};
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { goBack, navigate } = useNavigation();
  const [viewModel, setViewModel] = useState<(ShippingAgentViewModel) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);
  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };
  const [showIntegrationSettings, setShowIntegrationSettings] = useState(false);
  const [showReturnSettings, setShowReturnSettings] = useState(false);
  useEffect(() => {
    if (isLoading) showLoading();
    else hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (code) {
      setIsLoading(true);
      authService
        .get<ShippingAgentViewModel>(`/settingsApi/GetShippingAgent?code=${code}`)
        .then((response) =>
          setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null)
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")} : ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultModel: ShippingAgentViewModel & { originalValues: Partial<ShippingAgentViewModel>; hasChanges: boolean } = {
        createNew: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultModel.originalValues = { ...defaultModel };
      setViewModel(defaultModel);
    }
  }, [code, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return; // Prevent save if validation fails
    var newItem = viewModel.createNew ?? false;
    setIsLoading(true);
    try {
      const response = await authService.post<ShippingAgentViewModel>("/settingsApi/CreateOrUpdateAgent", viewModel);
      setViewModel({ ...response, originalValues: { ...response }, hasChanges: false });
      showSnackbar(t("dialog.savedSuccessfully"), "success");
      if (newItem) {
        navigate({ path: "/settings/shippingagent/edit", params: { code: response.code } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof ShippingAgentViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as ShippingAgentViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const handleCopy = async () => {
    if (viewModel?.hasChanges) showHasChangesError();
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      showSnackbar(t("dialog.copyPreliminaryEntity"), "warning");
      return;
    }
    setIsLoading(true);
    try {
      var response = await authService.post<ShippingAgentViewModel>(`/settingsApi/CopyAgent`, viewModel);
      setViewModel(response);
      showSnackbar(t("dialog.entityCopied"), "success");

      navigate({ path: "/settings/shippingagent/edit", params: { code: response.code } });

    } catch (error: any) {

      showSnackbar(`${t("dialog.copyFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/shippingagent/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeleteAgent`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");


      navigate({ path: "/settings/shippingagent/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };

  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }

    if (!viewModel.name || viewModel.name.trim() === "") {
      showSnackbar(t("help.nameRequired"), "warning");
      return false;
    }


    return true;
  };

  const handleClickServices = async () => {
    navigate({ path: "/settings/shippingservice/list", params: { agentCode: viewModel?.code } });
  };


  return (
    <>

      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.shippingAgent")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          maxWidth: "fit-content",
          mb: 2
        }}>
          <DefaultActions
            onSave={handleSave}
            onCopy={handleCopy}
            onDelete={handleDelete}

          />
        </Box>
        {viewModel && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label={t("common.code")}
                  value={viewModel.code || ""}
                  onChange={(e) => handleChange("code", e.target.value)}
                  fullWidth
                  margin="normal"
                  disabled={viewModel.createNew != true}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={t("common.name")}
                  value={viewModel.name || ""}
                  onChange={(e) => handleChange("name", e.target.value)}
                  fullWidth
                  margin="normal"
                  error={!viewModel.name || viewModel.name.trim() === ""}
                  helperText={!viewModel.name || viewModel.name.trim() === "" ? t("help.nameRequired") : ""}
                />
              </Grid>
              {viewModel.createNew != true && (
                <Grid item xs={12}>
                  <Button variant="contained" onClick={handleClickServices}
                    size="small"
                  >
                    {t("common.services")}
                  </Button>
                </Grid>
              )}
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.returnLink} onChange={(e) => handleChange("returnLink", e.target.checked)} />}
                  label={t("common.returnLink")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.clickAndCollect} onChange={(e) => handleChange("clickAndCollect", e.target.checked)} />}
                  label={t("common.clickAndCollect")}
                />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel
                  control={<Checkbox checked={!!viewModel.blocked} onChange={(e) => handleChange("blocked", e.target.checked)} />}
                  label={t("common.blocked")}
                />
              </Grid>
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showReturnSettings} onChange={(e) => setShowReturnSettings(e.target.checked)} />}
                label={t("common.showReturnSettings")}
              />
            </Grid>

            {/* Settings Section */}
            {showReturnSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnName")}
                      value={viewModel.returnName || ""}
                      onChange={(e) => handleChange("returnName", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnName2")}
                      value={viewModel.returnName2 || ""}
                      onChange={(e) => handleChange("returnName2", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnAddress")}
                      value={viewModel.returnAddress || ""}
                      onChange={(e) => handleChange("returnAddress", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnAddress2")}
                      value={viewModel.returnAddress2 || ""}
                      onChange={(e) => handleChange("returnAddress2", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnPostcode")}
                      value={viewModel.returnPostCode || ""}
                      onChange={(e) => handleChange("returnPostCode", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnCity")}
                      value={viewModel.returnCity || ""}
                      onChange={(e) => handleChange("returnCity", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.returnCounty")}
                      value={viewModel.returnCounty || ""}
                      onChange={(e) => handleChange("returnCounty", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <FormControl fullWidth margin="normal">
                      <CountryAutoComplete
                        key={viewModel.returnCountryCode || undefined}
                        valueId={viewModel.returnCountryCode || null}
                        onChange={(selected) => handleChange("returnCountryCode", selected?.value || "")}
                        label={t("common.returnCountry")}
                      />
                    </FormControl>
                  </Grid>

                </Box>
              </>
            )}

            <Grid item xs={12}>
              <FormControlLabel
                control={<Switch checked={showIntegrationSettings} onChange={(e) => setShowIntegrationSettings(e.target.checked)} />}
                label={t("common.showIntegrationSettings")}
              />
            </Grid>

            {/* Settings Section */}
            {showIntegrationSettings && (
              <>
                <Box
                  sx={{
                    backgroundColor: "secondary.main",
                    padding: 2,
                    borderRadius: 2,
                    width: "100%"
                  }}
                >


                  <Grid item xs={12}>

                    <FormControl fullWidth margin="normal">
                      <ShippingAgentExtSystemAutoComplete
                        key={viewModel.extSystem || undefined}
                        valueId={viewModel.extSystem || null}
                        onChange={(selected) => handleChange("extSystem", selected?.value || "")}
                        label={t("common.connectedSystem")}
                      />
                    </FormControl>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.integrationEndpoint")}
                      value={viewModel.integrationEndpointUrl || ""}
                      onChange={(e) => handleChange("integrationEndpointUrl", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.integrationUsername")}
                      value={viewModel.integrationEndpointUsername || ""}
                      onChange={(e) => handleChange("integrationEndpointUsername", e.target.value)}
                      fullWidth
                      margin="normal"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      label={t("common.integrationPassword")}
                      value={viewModel.integrationEndpointPassword || ""}
                      onChange={(e) => handleChange("integrationEndpointPassword", e.target.value)}
                      fullWidth
                      type="password"
                      margin="normal"
                    />
                  </Grid>



                </Box>
              </>
            )}
          </>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>


    </>
  );
};

export default ShippingAgentEditPage;
